import React, { useContext, useEffect, useState } from 'react'
import { useWindowSize } from 'react-use'
import { css } from '@emotion/core'
import { ErrorModal } from '@/components/molecules/qr-checkin/error-modal'
import { fetchSelfCheckOutReservations } from '@/apis/aipass'
import { decodeQr } from '@/libs/qr-decode'
import QrReader from 'react-qr-reader'
import { useHistory } from 'react-router-dom'
import { Footer } from '@/components/molecules/front-tablet/footer'
import { useTranslation } from 'react-i18next'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { SearchCheckOutReservation } from '@/models/reservation'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { SearchItem } from '@/models/search-setting'
import { LoaderContextCreator } from '@/contexts/loader'
import { reportError } from '@/components/apps/error-boundary'
import { notification } from 'antd'

export const QrCamera: React.FC = props => {
  const history = useHistory()
  const { t } = useTranslation()
  const windows = useWindowSize()
  const { state, saveState, stateErrorHandling } = useSelfCheckOutState()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [isError, setIsError] = useState<boolean>(false)
  const [isScanCompleted, setIsScanCompleted] = useState<boolean>(false)
  const [isInitializeComponent, setIsInitializeComponent] = useState(false)

  const onScan = async data => {
    if (isScanCompleted) {
      return
    }
    if (!data) {
      setIsError(false)
      return
    }
    decodeQr(data)
      .then(async (res: any) => {
        setIsLoading(true)
        setIsScanCompleted(true)
        let stayReservations: SearchCheckOutReservation[] = []
        try {
          stayReservations = (
            await fetchSelfCheckOutReservations({
              hotelId: state.hotelId,
              checkinId: res.id,
            })
          ).stayReservations
        } catch (e: any) {
          reportError(e)
        } finally {
          setIsLoading(false)
        }

        if (stayReservations.length === 0) {
          notification.warn({
            message: t('Reservation not found. Please try a different search method.'),
            style: { whiteSpace: 'pre-line' },
          })
          await new Promise(resolve => setTimeout(resolve, 3000))
          setIsScanCompleted(false)
          return
        }
        saveState({
          ...state,
          searchReservation: {
            condition: { mode: SearchItem.QR },
            result: {
              reservations: stayReservations,
            },
          },
        })
        history.push({ pathname: '/self-checkout/select-reservation' })
      })
      .catch(e => {
        console.log(e)
        setIsError(true)
      })
  }

  useEffect(() => {
    window.scrollTo(0, 0)
    if (!stateErrorHandling(state, 'setting')) {
      return
    }
    setIsInitializeComponent(true)
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }
  return (
    <FrontTabletLayout state={state}>
      {isError && <ErrorModal />}
      <div style={{ height: windows.height }} css={qrCameraWrapperStyle}>
        <QrReader showViewFinder={false} onScan={onScan} onError={() => setIsError(true)} constraints={{ facingMode: 'user' }} />
        <p css={qrCameraTitleStyle}>{t('Please align QR code within the frame to scan')}</p>
        <div css={qrCameraMaskStyle}>
          <div css={maskTopStyle} />
          <div css={cameraWrapperStyle}>
            <div css={maskLeftStyle} />
            <div css={qrCameraStyle} />
            <div css={maskRightStyle} />
          </div>
          <div css={maskBottomStyle} />
        </div>
        <Footer left={{ onClick: history.goBack, label: t('Back') }} />
      </div>
    </FrontTabletLayout>
  )
}

const qrCameraWrapperStyle = css({
  background: '#F2F2F2',
  minWidth: '100%',
  textAlign: 'center',
  section: {
    height: '100%',
  },
})

const qrCameraMaskStyle = css({
  width: '100%',
  height: 'calc(100vh - 90px)',
  position: 'absolute',
  top: 0,
})

const cameraWrapperStyle = css({
  display: 'flex',
  position: 'relative',
})

const maskTopStyle = css({
  background: 'rgba(0, 0, 0, 0.6)',
  height: 'calc((100vh - 90px - 525px)/2)',
})

const maskLeftStyle = css({
  background: 'rgba(0, 0, 0, 0.6)',
  height: 525,
  width: 'calc((100% - 525px)/2)',
  left: 0,
})

const qrCameraStyle = css({
  width: '525px',
  height: '525px',
  background: 'transparent',
  border: '2px solid #FFFFFF',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
})

const maskRightStyle = css({
  background: 'rgba(0, 0, 0, 0.6)',
  height: 525,
  width: 'calc((100% - 525px)/2)',
  position: 'absolute',
  right: 0,
})

const maskBottomStyle = css({
  background: 'rgba(0, 0, 0, 0.6)',
  height: 'calc((100vh - 90px - 525px)/2)',
})

const qrCameraTitleStyle = css({
  fontSize: 18,
  letterSpacing: '3.6px',
  fontWeight: 'bold',
  lineHeight: '28px',
  color: 'white',
  position: 'absolute',
  width: '100%',
  zIndex: 2,
  top: 'calc((100vh - 90px - 525px)/4)',
})
